import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Drawer,
  Badge,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  ContactsOutlined as ContactsIcon,
  Add as AddIcon,
  Note as NoteIcon,
  LocalShipping as ShippingIcon,
  AttachMoney as MoneyIcon,
  Assignment as TaskIcon,
  History as HistoryIcon,
  Route as RouteIcon,
  AccountTree as HierarchyIcon,
  Launch as LaunchIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  Info as InfoIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customerApi, activityApi, taskApi, tmsApi, userApi, offerAttachmentApi } from '../../api/client';
import TaskDrawer from '../../components/tasks/TaskDrawer';
import CustomerGeographyCard from '../../components/customers/CustomerGeographyCard';
import { Customer, Activity, Contact, Task, Transport, Offer, OfferAttachment, CustomerStatus, ActivityType } from '../../types';
import { useAuthStore } from '../../store/authStore';

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  NO_HISTORY: 'default',
  LEAD: 'default',
  CONTACTED: 'info',
  OFFER_SENT: 'warning',
  ACTIVE: 'success',
  INACTIVE: 'default',
  LOST: 'error',
};

const statusLabels: Record<string, string> = {
  NO_HISTORY: 'Ingen historik',
  LEAD: 'Lead',
  CONTACTED: 'Kontaktet',
  OFFER_SENT: 'Tilbud sendt',
  ACTIVE: 'Aktiv',
  INACTIVE: 'Inaktiv',
  LOST: 'Tabt',
};

const activityTypeLabels: Record<string, string> = {
  NOTE: 'Note',
  CALL: 'Opkald',
  EMAIL: 'Email',
  MEETING: 'Møde',
  STATUS_CHANGE: 'Statusændring',
  OFFER_CREATED: 'Tilbud oprettet',
  OFFER_SENT: 'Tilbud sendt',
  OFFER_ACCEPTED: 'Tilbud accepteret',
  OFFER_REJECTED: 'Tilbud afvist',
  TASK_CREATED: 'Opgave oprettet',
  TASK_COMPLETED: 'Opgave fuldført',
  SHIPMENT_CREATED: 'Forsendelse oprettet',
  SHIPMENT_DELIVERED: 'Forsendelse leveret',
};

const activityIcons: Record<string, React.ReactNode> = {
  NOTE: <NoteIcon fontSize="small" />,
  CALL: <PhoneIcon fontSize="small" />,
  EMAIL: <EmailIcon fontSize="small" />,
  MEETING: <PersonIcon fontSize="small" />,
  STATUS_CHANGE: <HistoryIcon fontSize="small" />,
  OFFER_CREATED: <MoneyIcon fontSize="small" />,
  SHIPMENT_CREATED: <ShippingIcon fontSize="small" />,
  TASK_CREATED: <TaskIcon fontSize="small" />,
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 1 }}>{children}</Box>}
    </div>
  );
}

const emptyCustomerForm = {
  name: '',
  cvr: '',
  email: '',
  phone: '',
  website: '',
  address: '',
  city: '',
  postalCode: '',
  country: '',
  notes: '',
  billingAddress: '',
  billingCity: '',
  billingPostalCode: '',
  billingCountry: '',
  assignedToId: '',
};

export const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [tabValue, setTabValue] = useState(0);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newContact, setNewContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    isPrimary: false,
  });

  // Edit/Delete state
  const [editActivityDialogOpen, setEditActivityDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [editActivityData, setEditActivityData] = useState({ title: '', description: '', outcome: '', nextSteps: '' });

  const [editContactDialogOpen, setEditContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [editContactData, setEditContactData] = useState({ firstName: '', lastName: '', email: '', phone: '', title: '', isPrimary: false });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [deletingItemType, setDeletingItemType] = useState<'activity' | 'contact' | 'offer'>('activity');

  const [editCustomerDialogOpen, setEditCustomerDialogOpen] = useState(false);
  const [customerFormData, setCustomerFormData] = useState(emptyCustomerForm);

  // Task state
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    dueDate: '',
    priority: 'MEDIUM' as string,
    assignedToId: '',
  });

  // Offer state — fields match Flowtale /api/v1/customerdb/offers 1:1
  // origin/destination kept as single combined strings for compact UI;
  // parsed into country/zipcode/city on save.
  const [showNewOfferRow, setShowNewOfferRow] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState<string | null>(null);
  const emptyOfferForm = {
    offerDate: '',
    contactId: '',
    origin: '',         // "DK 6330 Padborg"
    destination: '',    // "DE 20095 Hamburg"
    distanceKm: '',
    amount: '',
    currency: 'DKK',
    info: '',
  };
  const [offerFormData, setOfferFormData] = useState(emptyOfferForm);

  // Parse "DK 6330 Padborg" → { country, zipcode, city }
  const parseLocation = (input: string) => {
    const trimmed = input.trim();
    if (!trimmed) return { country: '', zipcode: '', city: '' };
    const parts = trimmed.split(/\s+/);
    let country = '';
    let zipcode = '';
    let cityParts = parts;
    // First token is country if it's 2-3 letters
    if (parts[0] && /^[A-Za-z]{2,3}$/.test(parts[0])) {
      country = parts[0].toUpperCase();
      cityParts = parts.slice(1);
    }
    // Next token is zipcode if it contains digits
    if (cityParts[0] && /\d/.test(cityParts[0])) {
      zipcode = cityParts[0];
      cityParts = cityParts.slice(1);
    }
    return { country, zipcode, city: cityParts.join(' ') };
  };

  const formatLocation = (country?: string | null, zipcode?: string | null, city?: string | null) =>
    [country, zipcode, city].filter(Boolean).join(' ');

  const canEditActivity = (activity: Activity) =>
    user?.id === activity.userId || user?.role === 'ADMIN' || user?.role === 'MANAGER';

  const { data: customer, isLoading, error } = useQuery({
    queryKey: ['customer', id],
    queryFn: async () => {
      const res = await customerApi.getById(id!);
      return res.data.data as Customer;
    },
    enabled: !!id,
  });

  const { data: activities } = useQuery({
    queryKey: ['customer', id, 'activities'],
    queryFn: async () => {
      const res = await activityApi.getByCustomer(id!);
      return res.data.data as Activity[];
    },
    enabled: !!id,
  });

  const { data: tasks } = useQuery({
    queryKey: ['customer', id, 'tasks'],
    queryFn: async () => {
      const res = await taskApi.getByCustomer(id!);
      return res.data.data as Task[];
    },
    enabled: !!id,
  });

  const { data: offers } = useQuery({
    queryKey: ['customer', id, 'offers'],
    queryFn: async () => {
      const res = await customerApi.getOffers(id!);
      return res.data.data as Offer[];
    },
    enabled: !!id,
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await userApi.getAll();
      return res.data.data as any[];
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: (description: string) =>
      activityApi.create({
        customerId: id!,
        type: 'NOTE',
        title: 'Note',
        description,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'activities'] });
      setNoteDialogOpen(false);
      setNewNote('');
    },
  });

  const addContactMutation = useMutation({
    mutationFn: (contact: typeof newContact) =>
      customerApi.addContact(id!, contact),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
      setContactDialogOpen(false);
      setNewContact({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        title: '',
        isPrimary: false,
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: CustomerStatus) =>
      customerApi.update(id!, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
    },
  });

  const removeParentMutation = useMutation({
    mutationFn: () => customerApi.removeParent(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
    },
  });

  // Edit/Delete mutations
  const updateActivityMutation = useMutation({
    mutationFn: ({ activityId, data }: { activityId: string; data: any }) =>
      activityApi.update(activityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'activities'] });
      setEditActivityDialogOpen(false);
      setEditingActivity(null);
    },
  });

  const deleteActivityMutation = useMutation({
    mutationFn: (activityId: string) => activityApi.delete(activityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'activities'] });
      setDeleteConfirmOpen(false);
      setDeletingItemId(null);
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: ({ contactId, data }: { contactId: string; data: any }) =>
      customerApi.updateContact(id!, contactId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
      setEditContactDialogOpen(false);
      setEditingContact(null);
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: (contactId: string) => customerApi.deleteContact(id!, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
      setDeleteConfirmOpen(false);
      setDeletingItemId(null);
    },
  });

  const createTaskMutation = useMutation({
    mutationFn: (data: typeof newTask) =>
      taskApi.create({
        ...data,
        dueDate: new Date(data.dueDate).toISOString(),
        customerId: id!,
        assignedToId: data.assignedToId || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'tasks'] });
      setTaskDialogOpen(false);
      setNewTask({ title: '', description: '', dueDate: '', priority: 'MEDIUM', assignedToId: '' });
    },
    onError: (error: any) => {
      console.error('Task creation failed:', error);
      alert('Kunne ikke oprette opgave: ' + (error?.response?.data?.error || error.message));
    },
  });

  const [drawerTask, setDrawerTask] = useState<Task | null>(null);

  const completeTaskMutation = useMutation({
    mutationFn: (taskId: string) => taskApi.complete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'tasks'] });
    },
  });

  const uncompleteTaskMutation = useMutation({
    mutationFn: (taskId: string) => taskApi.uncomplete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'tasks'] });
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) => taskApi.delete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'tasks'] });
      setDrawerTask(null);
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id: taskId, data }: { id: string; data: any }) => taskApi.update(taskId, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'tasks'] });
      if (drawerTask) setDrawerTask({ ...drawerTask, ...(res.data?.data || {}) });
    },
  });

  const updateCustomerMutation = useMutation({
    mutationFn: (data: any) => customerApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
      setEditCustomerDialogOpen(false);
    },
  });

  // Offer mutations
  const resetOfferForm = () => {
    setOfferFormData(emptyOfferForm);
    setShowNewOfferRow(false);
    setEditingOfferId(null);
  };

  const createOfferMutation = useMutation({
    mutationFn: (data: any) => customerApi.createOffer(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'offers'] });
      resetOfferForm();
    },
  });

  const updateOfferMutation = useMutation({
    mutationFn: ({ offerId, data }: { offerId: string; data: any }) =>
      customerApi.updateOffer(id!, offerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'offers'] });
      resetOfferForm();
    },
  });

  const updateOfferStatusMutation = useMutation({
    mutationFn: ({ offerId, status }: { offerId: string; status: string }) =>
      customerApi.updateOfferStatus(id!, offerId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'offers'] });
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
    },
  });

  const deleteOfferMutation = useMutation({
    mutationFn: (offerId: string) => customerApi.deleteOffer(id!, offerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'offers'] });
      setDeleteConfirmOpen(false);
      setDeletingItemId(null);
    },
  });

  // Offer attachments: files dragged onto an offer row (or picked via "Vedhæft fil")
  const [dragOverOfferId, setDragOverOfferId] = useState<string | null>(null);
  const [attachmentsOpenOfferId, setAttachmentsOpenOfferId] = useState<string | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [attachmentToDelete, setAttachmentToDelete] = useState<{ offerId: string; attachment: OfferAttachment } | null>(null);
  const [fileInputOfferId, setFileInputOfferId] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const canEditAttachments = user?.role === 'ADMIN' || user?.role === 'MANAGER' || user?.role === 'DISPATCHER';

  const uploadAttachmentsMutation = useMutation({
    mutationFn: ({ offerId, files }: { offerId: string; files: File[] }) =>
      offerAttachmentApi.upload(id!, offerId, files),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'offers'] });
      setAttachmentsOpenOfferId(vars.offerId);
      setAttachmentError(null);
    },
    onError: (err: any) => {
      setAttachmentError(err?.response?.data?.error || 'Upload af fil fejlede');
    },
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: ({ offerId, attachmentId }: { offerId: string; attachmentId: string }) =>
      offerAttachmentApi.delete(id!, offerId, attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', id, 'offers'] });
      setAttachmentToDelete(null);
    },
    onError: (err: any) => {
      setAttachmentError(err?.response?.data?.error || 'Sletning af vedhæftning fejlede');
      setAttachmentToDelete(null);
    },
  });

  const handleOfferFiles = (offerId: string, fileList: FileList | File[] | null) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    uploadAttachmentsMutation.mutate({ offerId, files });
  };

  const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');

  const handleOfferDragOver = (e: React.DragEvent, offerId: string) => {
    if (!canEditAttachments || !isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    if (dragOverOfferId !== offerId) setDragOverOfferId(offerId);
  };

  const handleOfferDragLeave = (e: React.DragEvent, offerId: string) => {
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    setDragOverOfferId((current) => (current === offerId ? null : current));
  };

  const handleOfferDrop = (e: React.DragEvent, offerId: string) => {
    if (!canEditAttachments || !isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverOfferId(null);
    handleOfferFiles(offerId, e.dataTransfer.files);
  };

  const openAttachmentPicker = (offerId: string) => {
    setFileInputOfferId(offerId);
    fileInputRef.current?.click();
  };

  const handleDownloadAttachment = async (offerId: string, attachment: OfferAttachment) => {
    try {
      await offerAttachmentApi.download(id!, offerId, attachment.id, attachment.fileName);
    } catch (err: any) {
      setAttachmentError(err?.response?.data?.error || 'Download fejlede');
    }
  };

  const formatFileSize = (bytes: number) =>
    bytes >= 1024 * 1024
      ? `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
      : `${Math.max(1, Math.round(bytes / 1024))} KB`;

  const handleStartNewOffer = () => {
    setEditingOfferId(null);
    setOfferFormData({
      ...emptyOfferForm,
      offerDate: new Date().toISOString().split('T')[0],
    });
    setShowNewOfferRow(true);
  };

  const handleStartEditOffer = (offer: Offer) => {
    setShowNewOfferRow(false);
    setEditingOfferId(offer.id);
    setOfferFormData({
      offerDate: offer.offerDate ? new Date(offer.offerDate).toISOString().split('T')[0] : '',
      contactId: offer.contactId || '',
      origin: formatLocation(offer.originCountry, offer.originZipcode, offer.originCity),
      destination: formatLocation(offer.destinationCountry, offer.destinationZipcode, offer.destinationCity),
      distanceKm: offer.distanceKm != null ? String(offer.distanceKm) : '',
      amount: offer.amount != null ? String(offer.amount) : '',
      currency: offer.currency || 'DKK',
      info: offer.info || offer.description || '',
    });
  };

  const handleSaveOffer = () => {
    const origin = parseLocation(offerFormData.origin);
    const destination = parseLocation(offerFormData.destination);

    // Build a route-based title for backwards compatibility with the existing
    // `title` column (which is NOT NULL in the schema)
    const originStr = formatLocation(origin.country, origin.zipcode, origin.city);
    const destStr = formatLocation(destination.country, destination.zipcode, destination.city);
    const title = originStr && destStr
      ? `${originStr} → ${destStr}`
      : (offerFormData.info || 'Tilbud');

    const data: any = {
      title,
      offerDate: offerFormData.offerDate ? new Date(offerFormData.offerDate).toISOString() : undefined,
      contactId: offerFormData.contactId || null,
      originCountry: origin.country || undefined,
      originZipcode: origin.zipcode || undefined,
      originCity: origin.city || undefined,
      destinationCountry: destination.country || undefined,
      destinationZipcode: destination.zipcode || undefined,
      destinationCity: destination.city || undefined,
      distanceKm: offerFormData.distanceKm ? Number(offerFormData.distanceKm) : undefined,
      amount: offerFormData.amount ? Number(offerFormData.amount) : undefined,
      currency: offerFormData.currency || 'DKK',
      info: offerFormData.info || undefined,
      description: offerFormData.info || undefined,
    };
    if (editingOfferId) {
      updateOfferMutation.mutate({ offerId: editingOfferId, data });
    } else {
      createOfferMutation.mutate(data);
    }
  };

  // Handlers
  const handleEditActivity = (activity: Activity) => {
    setEditingActivity(activity);
    setEditActivityData({
      title: activity.title || '',
      description: activity.description || '',
      outcome: activity.outcome || '',
      nextSteps: activity.nextSteps || '',
    });
    setEditActivityDialogOpen(true);
  };

  const handleDeleteConfirm = (itemId: string, type: 'activity' | 'contact' | 'offer') => {
    setDeletingItemId(itemId);
    setDeletingItemType(type);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteExecute = () => {
    if (!deletingItemId) return;
    if (deletingItemType === 'activity') {
      deleteActivityMutation.mutate(deletingItemId);
    } else if (deletingItemType === 'contact') {
      deleteContactMutation.mutate(deletingItemId);
    } else if (deletingItemType === 'offer') {
      deleteOfferMutation.mutate(deletingItemId);
    }
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setEditContactData({
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      email: contact.email || '',
      phone: contact.phone || '',
      title: contact.title || '',
      isPrimary: contact.isPrimary || false,
    });
    setEditContactDialogOpen(true);
  };

  const handleOpenCustomerEdit = () => {
    if (!customer) return;
    setCustomerFormData({
      name: customer.name || '',
      cvr: customer.cvr || '',
      email: customer.email || '',
      phone: customer.phone || '',
      website: customer.website || '',
      address: customer.address || '',
      city: customer.city || '',
      postalCode: customer.postalCode || '',
      country: customer.country || '',
      notes: customer.notes || '',
      billingAddress: customer.billingAddress || '',
      billingCity: customer.billingCity || '',
      billingPostalCode: customer.billingPostalCode || '',
      billingCountry: customer.billingCountry || '',
      assignedToId: customer.assignedToId || '',
    });
    setEditCustomerDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !customer) {
    return (
      <Alert severity="error">
        Kunne ikke hente kundeoplysninger
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <IconButton size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon sx={{ fontSize: '1.1rem' }} />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1rem' }}>
            {customer.name}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
            <Chip
              size="small"
              label={statusLabels[customer.status] || customer.status}
              color={statusColors[customer.status] || 'default'}
              sx={{ borderRadius: 1, height: 18, fontSize: '0.625rem' }}
            />
            {customer.isPreviousCustomer && (
              <Chip
                size="small"
                label="Tidl. kunde"
                variant="outlined"
                sx={{ borderRadius: 1, height: 18, fontSize: '0.625rem' }}
              />
            )}
            {(customer as any).source && (customer as any).source !== 'manual' && (
              <Chip
                size="small"
                label={`Kilde: ${(customer as any).source === 'transpas' ? 'Transpas' : (customer as any).source}`}
                variant="outlined"
                color="info"
                sx={{ borderRadius: 1, height: 18, fontSize: '0.625rem' }}
              />
            )}
            {customer.segment && (
              <Chip
                size="small"
                label={customer.segment.name}
                variant="outlined"
                sx={{ borderRadius: 1, height: 18, fontSize: '0.625rem' }}
              />
            )}
          </Box>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<EditIcon sx={{ fontSize: '0.9rem' }} />}
          sx={{ borderRadius: 1 }}
          onClick={handleOpenCustomerEdit}
        >
          Rediger
        </Button>
      </Box>

      <Grid container spacing={1.5}>
        {/* Left Column - Info Cards */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Contact Info */}
          <Card sx={{ borderRadius: 1, mb: 1.5 }}>
            <CardContent sx={{ p: '10px 12px !important' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Kontaktoplysninger
              </Typography>

              <Box mt={1}>
                {customer.phone && (
                  <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
                    <PhoneIcon sx={{ fontSize: '0.85rem' }} color="action" />
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{customer.phone}</Typography>
                  </Box>
                )}

                {customer.email && (
                  <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
                    <EmailIcon sx={{ fontSize: '0.85rem' }} color="action" />
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{customer.email}</Typography>
                  </Box>
                )}

                {customer.website && (
                  <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
                    <BusinessIcon sx={{ fontSize: '0.85rem' }} color="action" />
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{customer.website}</Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Adresse
              </Typography>
              <Box display="flex" alignItems="flex-start" gap={0.75} mt={0.5}>
                <LocationIcon sx={{ fontSize: '0.85rem' }} color="action" />
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                  {customer.address && `${customer.address}, `}
                  {customer.postalCode} {customer.city}
                  {customer.country && `, ${customer.country}`}
                </Typography>
              </Box>

              {customer.cvr && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    CVR
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', mt: 0.25 }}>{customer.cvr}</Typography>
                </>
              )}
            </CardContent>
          </Card>

          {/* Hierarchy */}
          {(customer.parent || (customer.children && customer.children.length > 0)) && (
            <Card sx={{ borderRadius: 1, mb: 1.5 }}>
              <CardContent sx={{ p: '10px 12px !important' }}>
                {customer.parent ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Hovedkontor
                    </Typography>
                    <Box
                      display="flex" alignItems="center" gap={0.5} mt={0.5}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, p: 0.5 }}
                      onClick={() => navigate(`/customers/${customer.parent!.id}`)}
                    >
                      <HierarchyIcon sx={{ fontSize: '0.85rem', color: 'primary.main' }} />
                      <Box>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{customer.parent.name}</Typography>
                        {customer.parent.city && (
                          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{customer.parent.city}</Typography>
                        )}
                      </Box>
                      <LaunchIcon sx={{ fontSize: '0.65rem', color: 'text.secondary', ml: 'auto' }} />
                    </Box>
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      onClick={() => removeParentMutation.mutate()}
                      disabled={removeParentMutation.isPending}
                      sx={{ fontSize: '0.6rem', mt: 0.5, textTransform: 'none' }}
                    >
                      Fjern fra koncern
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Koncern ({customer.children!.length} {customer.children!.length === 1 ? 'afdeling' : 'afdelinger'})
                    </Typography>
                    {customer.children!.map((child: any) => (
                      <Box
                        key={child.id}
                        display="flex" alignItems="center" gap={0.5} mt={0.5}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, p: 0.5 }}
                        onClick={() => navigate(`/customers/${child.id}`)}
                      >
                        <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>&#x2514;</Typography>
                        <Box flex={1}>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>{child.name}</Typography>
                          {child.city && (
                            <Typography sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>{child.city}</Typography>
                          )}
                        </Box>
                        <LaunchIcon sx={{ fontSize: '0.6rem', color: 'text.secondary' }} />
                      </Box>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Status */}
          <Card sx={{ borderRadius: 1, mb: 1.5 }}>
            <CardContent sx={{ p: '10px 12px !important' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.75, display: 'block' }}>
                Status
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={customer.status}
                  onChange={(e) => updateStatusMutation.mutate(e.target.value as CustomerStatus)}
                  disabled={updateStatusMutation.isPending}
                  sx={{ fontSize: '0.75rem' }}
                >
                  <MenuItem value="NO_HISTORY">Ingen historik</MenuItem>
                  <MenuItem value="LEAD">Lead</MenuItem>
                  <MenuItem value="CONTACTED">Kontaktet</MenuItem>
                  <MenuItem value="OFFER_SENT">Tilbud sendt</MenuItem>
                  <MenuItem value="ACTIVE">Aktiv</MenuItem>
                  <MenuItem value="INACTIVE">Inaktiv</MenuItem>
                  <MenuItem value="LOST">Tabt</MenuItem>
                </Select>
              </FormControl>

              {customer.assignedTo && (
                <Box mt={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    Ansvarlig
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem' }}>
                      {customer.assignedTo.firstName?.[0]}{customer.assignedTo.lastName?.[0]}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {customer.assignedTo.firstName} {customer.assignedTo.lastName}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Contacts */}
          <Card sx={{ borderRadius: 1 }}>
            <CardContent sx={{ p: '10px 12px !important' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ContactsIcon sx={{ fontSize: '0.85rem', color: '#208040' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    Kontaktpersoner
                  </Typography>
                </Box>
                <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setContactDialogOpen(true)}>
                  <AddIcon sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Box>

              {customer.contacts && customer.contacts.length > 0 ? (
                <List dense disablePadding>
                  {customer.contacts.map((contact: Contact) => (
                    <ListItem
                      key={contact.id}
                      disableGutters
                      sx={{ py: 0.25 }}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0 }}>
                          <Tooltip title="Rediger">
                            <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleEditContact(contact)}>
                              <EditIcon sx={{ fontSize: '0.7rem' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Slet">
                            <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleDeleteConfirm(contact.id, 'contact')}>
                              <DeleteIcon sx={{ fontSize: '0.7rem' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    >
                      <ListItemAvatar sx={{ minWidth: 28 }}>
                        <Avatar sx={{ width: 22, height: 22, fontSize: '0.55rem' }}>
                          {contact.firstName?.[0]}{contact.lastName?.[0]}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>{contact.firstName} {contact.lastName}</span>
                            {contact.isPrimary && (
                              <Chip size="small" label="Primær" sx={{ borderRadius: 1, height: 15, fontSize: '0.5rem' }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box component="span" sx={{ display: 'block' }}>
                            {contact.title && (
                              <Box component="span" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.6rem' }}>
                                {contact.title}
                              </Box>
                            )}
                            {contact.email && (
                              <Box
                                component="a"
                                href={`mailto:${contact.email}`}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: '#208040', textDecoration: 'none', fontSize: '0.65rem', '&:hover': { textDecoration: 'underline' } }}
                              >
                                <EmailIcon sx={{ fontSize: '0.7rem' }} />
                                {contact.email}
                              </Box>
                            )}
                            {(contact.phone || (contact as any).mobile) && (
                              <Box
                                component="a"
                                href={`tel:${(contact.phone || (contact as any).mobile || '').replace(/\s/g, '')}`}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: '#208040', textDecoration: 'none', fontSize: '0.65rem', '&:hover': { textDecoration: 'underline' } }}
                              >
                                <PhoneIcon sx={{ fontSize: '0.7rem' }} />
                                {contact.phone || (contact as any).mobile}
                              </Box>
                            )}
                          </Box>
                        }
                        primaryTypographyProps={{ fontSize: '0.75rem', fontWeight: 500, component: 'div' }}
                        secondaryTypographyProps={{ component: 'div', sx: { mt: 0.25 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Ingen kontaktpersoner
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Geography from transports (afsender/modtager) */}
          <CustomerGeographyCard customerId={id!} />
        </Grid>

        {/* Right Column - Tabs */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ borderRadius: 1 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ minHeight: 36 }}>
                <Tab label="Aktivitet" sx={{ minHeight: 36, py: 0.5, fontSize: '0.75rem' }} />
                <Tab label="Opgaver" sx={{ minHeight: 36, py: 0.5, fontSize: '0.75rem' }} />
                <Tab label="Tilbud" sx={{ minHeight: 36, py: 0.5, fontSize: '0.75rem' }} />
                <Tab label="Forsendelser" sx={{ minHeight: 36, py: 0.5, fontSize: '0.75rem' }} />
                <Tab icon={<RouteIcon sx={{ fontSize: '0.85rem' }} />} iconPosition="start" label="Faste ruter" sx={{ minHeight: 36, py: 0.5, fontSize: '0.75rem' }} />
              </Tabs>
            </Box>

            {/* Activity Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box px={1.5}>
                <Box display="flex" justifyContent="flex-end" mb={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: '0.85rem' }} />}
                    onClick={() => setNoteDialogOpen(true)}
                    sx={{ borderRadius: 1, fontSize: '0.7rem' }}
                  >
                    Tilføj note
                  </Button>
                </Box>

                {activities && activities.length > 0 ? (
                  <List dense disablePadding>
                    {activities.map((activity: Activity) => (
                      <ListItem
                        key={activity.id}
                        alignItems="flex-start"
                        disableGutters
                        sx={{ py: 0.5 }}
                        secondaryAction={
                          canEditActivity(activity) ? (
                            <Box sx={{ display: 'flex', gap: 0, mt: 0.5 }}>
                              <Tooltip title="Rediger">
                                <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleEditActivity(activity)}>
                                  <EditIcon sx={{ fontSize: '0.7rem' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Slet">
                                <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleDeleteConfirm(activity.id, 'activity')}>
                                  <DeleteIcon sx={{ fontSize: '0.7rem' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : undefined
                        }
                      >
                        <ListItemAvatar sx={{ minWidth: 28 }}>
                          <Avatar sx={{ width: 22, height: 22, bgcolor: 'primary.light', '& svg': { fontSize: '0.75rem' } }}>
                            {activityIcons[activity.type] || <NoteIcon fontSize="small" />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          secondaryTypographyProps={{ component: 'div' }}
                          primary={
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.7rem' }}>
                                {activityTypeLabels[activity.type] || activity.type}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                {new Date(activity.createdAt).toLocaleString('da-DK')}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <>
                              {activity.description && (
                                <Typography variant="body2" color="text.primary" component="div" sx={{ mt: 0.25, fontSize: '0.7rem' }}>
                                  {activity.description}
                                </Typography>
                              )}
                              {activity.outcome && (
                                <Box component="div" sx={{ mt: 0.5, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: '0.6rem',
                                      fontWeight: 700,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.04em',
                                      color: '#208040',
                                      flexShrink: 0,
                                      mt: '1px',
                                    }}
                                  >
                                    Resultat:
                                  </Typography>
                                  <Typography
                                    component="span"
                                    sx={{ fontSize: '0.7rem', color: 'text.primary' }}
                                  >
                                    {activity.outcome}
                                  </Typography>
                                </Box>
                              )}
                              {activity.nextSteps && (
                                <Box component="div" sx={{ mt: 0.25, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: '0.6rem',
                                      fontWeight: 700,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.04em',
                                      color: '#8A5A10',
                                      flexShrink: 0,
                                      mt: '1px',
                                    }}
                                  >
                                    Næste:
                                  </Typography>
                                  <Typography
                                    component="span"
                                    sx={{ fontSize: '0.7rem', color: 'text.primary' }}
                                  >
                                    {activity.nextSteps}
                                  </Typography>
                                </Box>
                              )}
                              {activity.user && (
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ fontSize: '0.6rem', mt: 0.5 }}>
                                  af {activity.user.firstName} {activity.user.lastName}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={2} sx={{ fontSize: '0.75rem' }}>
                    Ingen aktiviteter endnu
                  </Typography>
                )}
              </Box>
            </TabPanel>

            {/* Tasks Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box px={1.5}>
                <Box display="flex" justifyContent="flex-end" mb={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon sx={{ fontSize: '0.8rem' }} />}
                    onClick={() => setTaskDialogOpen(true)}
                    sx={{ borderRadius: 1, fontSize: '0.7rem', textTransform: 'none' }}
                  >
                    Opret opgave
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ py: 0.75, fontSize: '0.65rem', width: 32 }}></TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.65rem' }}>Opgave</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.65rem' }}>Prioritet</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.65rem' }}>Forfald</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.65rem' }}>Status</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.65rem', width: 40 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tasks && tasks.length > 0 ? (
                        tasks.map((task: Task) => (
                          <TableRow
                            key={task.id}
                            hover
                            onClick={() => setDrawerTask(task)}
                            sx={{ '& td': { py: 0.5, fontSize: '0.7rem' }, opacity: task.isCompleted ? 0.6 : 1, cursor: 'pointer' }}
                          >
                            <TableCell sx={{ px: 0.5 }} onClick={(e) => e.stopPropagation()}>
                              <Tooltip title={task.isCompleted ? 'Marker som åben' : 'Marker som fuldført'}>
                                <IconButton
                                  size="small"
                                  sx={{ p: 0.25 }}
                                  onClick={() => task.isCompleted
                                    ? uncompleteTaskMutation.mutate(task.id)
                                    : completeTaskMutation.mutate(task.id)
                                  }
                                >
                                  {task.isCompleted
                                    ? <CheckCircleIcon sx={{ fontSize: '0.9rem' }} color="success" />
                                    : <CheckCircleOutlineIcon sx={{ fontSize: '0.9rem' }} color="action" />
                                  }
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ textDecoration: task.isCompleted ? 'line-through' : 'none' }}>
                              {task.title}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={
                                  task.priority === 'URGENT' ? 'Urgent' :
                                  task.priority === 'HIGH' ? 'Høj' :
                                  task.priority === 'MEDIUM' ? 'Medium' : 'Lav'
                                }
                                color={
                                  task.priority === 'URGENT' ? 'error' :
                                  task.priority === 'HIGH' ? 'warning' :
                                  task.priority === 'MEDIUM' ? 'info' : 'default'
                                }
                                sx={{ borderRadius: 1, height: 16, fontSize: '0.55rem' }}
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(task.dueDate).toLocaleDateString('da-DK')}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={task.isCompleted ? 'Fuldført' : 'Åben'}
                                color={task.isCompleted ? 'success' : 'default'}
                                sx={{ borderRadius: 1, height: 16, fontSize: '0.55rem' }}
                              />
                            </TableCell>
                            <TableCell sx={{ px: 0.5 }} onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="Rediger">
                                <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setDrawerTask(task)}>
                                  <EditIcon sx={{ fontSize: '0.7rem' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Slet">
                                <IconButton
                                  size="small"
                                  sx={{ p: 0.25 }}
                                  onClick={() => deleteTaskMutation.mutate(task.id)}
                                >
                                  <DeleteIcon sx={{ fontSize: '0.7rem' }} color="error" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="text.secondary" py={1.5} sx={{ fontSize: '0.75rem' }}>
                              Ingen opgaver
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </TabPanel>

            {/* Offers Tab */}
            <TabPanel value={tabValue} index={2}>
              <Box px={1.5}>
                <Box display="flex" justifyContent="flex-end" mb={1}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleStartNewOffer}
                    disabled={showNewOfferRow}
                    sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                  >
                    Nyt tilbud
                  </Button>
                </Box>
                {attachmentError && (
                  <Alert severity="error" onClose={() => setAttachmentError(null)} sx={{ mb: 1, py: 0, fontSize: '0.75rem' }}>
                    {attachmentError}
                  </Alert>
                )}
                {canEditAttachments && offers && offers.length > 0 && (
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mb: 0.5 }}>
                    Tip: Træk filer direkte over på et tilbud for at vedhæfte dem.
                  </Typography>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tif,.tiff,.heic,.doc,.docx,.xls,.xlsx,.xlsm,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.eml,.msg"
                  onChange={(e) => {
                    if (fileInputOfferId) handleOfferFiles(fileInputOfferId, e.target.files);
                    e.target.value = '';
                  }}
                />
                <Dialog open={!!attachmentToDelete} onClose={() => setAttachmentToDelete(null)} maxWidth="xs" fullWidth>
                  <DialogTitle sx={{ fontSize: '0.9rem' }}>Slet vedhæftning?</DialogTitle>
                  <DialogContent>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      Vil du slette "{attachmentToDelete?.attachment.fileName}" fra tilbuddet?
                    </Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button size="small" onClick={() => setAttachmentToDelete(null)} sx={{ textTransform: 'none' }}>
                      Annuller
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      disabled={deleteAttachmentMutation.isPending}
                      onClick={() =>
                        attachmentToDelete &&
                        deleteAttachmentMutation.mutate({ offerId: attachmentToDelete.offerId, attachmentId: attachmentToDelete.attachment.id })
                      }
                      sx={{ textTransform: 'none' }}
                    >
                      Slet
                    </Button>
                  </DialogActions>
                </Dialog>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ py: 0.75, fontSize: '0.6rem' }}>Dato</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.6rem' }}>Kontakt</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.6rem' }}>Afhentning</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.6rem' }}>Levering</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.6rem' }} align="right">Km</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.6rem' }} align="right">Pris</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.6rem' }}>Info</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.6rem' }}>Status</TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: '0.6rem' }} align="right">Handlinger</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {showNewOfferRow && (
                        <OfferEditRow
                          isNew
                          formData={offerFormData}
                          setFormData={setOfferFormData}
                          contacts={customer.contacts || []}
                          onSave={handleSaveOffer}
                          onCancel={resetOfferForm}
                          isPending={createOfferMutation.isPending}
                        />
                      )}
                      {offers && offers.length > 0 ? (
                        offers.map((offer: Offer) => {
                          const isEditing = editingOfferId === offer.id;
                          if (isEditing) {
                            return (
                              <OfferEditRow
                                key={offer.id}
                                formData={offerFormData}
                                setFormData={setOfferFormData}
                                contacts={customer.contacts || []}
                                onSave={handleSaveOffer}
                                onCancel={resetOfferForm}
                                isPending={updateOfferMutation.isPending}
                              />
                            );
                          }
                          const contactName = offer.contactId
                            ? customer.contacts?.find((c: Contact) => c.id === offer.contactId)
                            : null;
                          const origin = [offer.originZipcode, offer.originCity, offer.originCountry].filter(Boolean).join(' ');
                          const destination = [offer.destinationZipcode, offer.destinationCity, offer.destinationCountry].filter(Boolean).join(' ');
                          const isDragTarget = dragOverOfferId === offer.id;
                          const attachmentsOpen = attachmentsOpenOfferId === offer.id;
                          const attachmentCount = offer.attachments?.length || 0;
                          return (
                            <React.Fragment key={offer.id}>
                            <TableRow
                              hover
                              onDragEnter={(e) => handleOfferDragOver(e, offer.id)}
                              onDragOver={(e) => handleOfferDragOver(e, offer.id)}
                              onDragLeave={(e) => handleOfferDragLeave(e, offer.id)}
                              onDrop={(e) => handleOfferDrop(e, offer.id)}
                              sx={{
                                '& td': { py: 0.5, fontSize: '0.7rem' },
                                ...(isDragTarget
                                  ? { outline: '2px dashed', outlineColor: 'primary.main', outlineOffset: -2, bgcolor: 'action.selected' }
                                  : {}),
                              }}
                            >
                              <TableCell>
                                {offer.offerDate
                                  ? new Date(offer.offerDate).toLocaleDateString('da-DK')
                                  : new Date(offer.createdAt).toLocaleDateString('da-DK')}
                              </TableCell>
                              <TableCell>
                                {contactName ? `${contactName.firstName} ${contactName.lastName}` : '-'}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {offer.tmsReference && (
                                    <Chip
                                      label="TMS"
                                      size="small"
                                      variant="outlined"
                                      color="info"
                                      sx={{ height: 15, fontSize: '0.5rem' }}
                                    />
                                  )}
                                  <span>{origin || offer.title || '-'}</span>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {destination || (origin ? '-' : '')}
                              </TableCell>
                              <TableCell align="right">{offer.distanceKm ?? '-'}</TableCell>
                              <TableCell align="right">
                                {offer.amount
                                  ? `${Number(offer.amount).toLocaleString('da-DK')} ${offer.currency}`
                                  : '-'}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {offer.info || offer.description || '-'}
                              </TableCell>
                              <TableCell>
                                <FormControl size="small" variant="standard" sx={{ minWidth: 80 }}>
                                  <Select
                                    value={offer.status}
                                    onChange={(e) => updateOfferStatusMutation.mutate({ offerId: offer.id, status: e.target.value })}
                                    sx={{ fontSize: '0.65rem' }}
                                    disableUnderline
                                  >
                                    <MenuItem value="DRAFT" sx={{ fontSize: '0.7rem' }}>Kladde</MenuItem>
                                    <MenuItem value="PENDING" sx={{ fontSize: '0.7rem' }}>Afventer</MenuItem>
                                    <MenuItem value="ACCEPTED" sx={{ fontSize: '0.7rem' }}>Accepteret</MenuItem>
                                    <MenuItem value="REJECTED" sx={{ fontSize: '0.7rem' }}>Afvist</MenuItem>
                                    <MenuItem value="EXPIRED" sx={{ fontSize: '0.7rem' }}>Udløbet</MenuItem>
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                <Tooltip title={attachmentCount > 0 ? `${attachmentCount} vedhæftet fil(er)` : 'Vedhæftede filer'}>
                                  <IconButton
                                    size="small"
                                    color={attachmentsOpen ? 'primary' : 'default'}
                                    onClick={() => setAttachmentsOpenOfferId(attachmentsOpen ? null : offer.id)}
                                  >
                                    <Badge
                                      badgeContent={attachmentCount}
                                      color="primary"
                                      sx={{ '& .MuiBadge-badge': { fontSize: '0.5rem', height: 13, minWidth: 13, px: 0.4 } }}
                                    >
                                      <AttachFileIcon sx={{ fontSize: 14 }} />
                                    </Badge>
                                  </IconButton>
                                </Tooltip>
                                <IconButton size="small" onClick={() => handleStartEditOffer(offer)}>
                                  <EditIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDeleteConfirm(offer.id, 'offer')} color="error">
                                  <DeleteIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                            {(attachmentsOpen || isDragTarget) && (
                              <TableRow
                                onDragEnter={(e) => handleOfferDragOver(e, offer.id)}
                                onDragOver={(e) => handleOfferDragOver(e, offer.id)}
                                onDragLeave={(e) => handleOfferDragLeave(e, offer.id)}
                                onDrop={(e) => handleOfferDrop(e, offer.id)}
                              >
                                <TableCell
                                  colSpan={9}
                                  sx={{
                                    py: 0.75,
                                    px: 1.5,
                                    bgcolor: isDragTarget ? 'action.selected' : 'action.hover',
                                    borderLeft: '3px solid',
                                    borderLeftColor: 'primary.main',
                                  }}
                                >
                                  {isDragTarget ? (
                                    <Box display="flex" alignItems="center" gap={1} py={0.5} color="primary.main">
                                      <CloudUploadIcon sx={{ fontSize: 18 }} />
                                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                        Slip filer her for at vedhæfte
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Box>
                                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={attachmentCount > 0 ? 0.5 : 0}>
                                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary' }}>
                                          Vedhæftede filer ({attachmentCount})
                                        </Typography>
                                        {canEditAttachments && (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<AttachFileIcon sx={{ fontSize: 12 }} />}
                                            onClick={() => openAttachmentPicker(offer.id)}
                                            disabled={uploadAttachmentsMutation.isPending}
                                            sx={{ fontSize: '0.65rem', textTransform: 'none', py: 0, minHeight: 22 }}
                                          >
                                            {uploadAttachmentsMutation.isPending && uploadAttachmentsMutation.variables?.offerId === offer.id
                                              ? 'Uploader...'
                                              : 'Vedhæft fil'}
                                          </Button>
                                        )}
                                      </Box>
                                      {attachmentCount === 0 ? (
                                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                          {canEditAttachments
                                            ? 'Ingen filer endnu. Træk filer hertil eller klik på "Vedhæft fil".'
                                            : 'Ingen filer vedhæftet.'}
                                        </Typography>
                                      ) : (
                                        <Box display="flex" flexDirection="column" gap={0.25}>
                                          {offer.attachments!.map((att) => (
                                            <Box key={att.id} display="flex" alignItems="center" gap={1}>
                                              <AttachFileIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                              <Typography
                                                component="button"
                                                type="button"
                                                onClick={() => handleDownloadAttachment(offer.id, att)}
                                                sx={{
                                                  fontSize: '0.7rem',
                                                  color: 'primary.main',
                                                  background: 'none',
                                                  border: 0,
                                                  p: 0,
                                                  cursor: 'pointer',
                                                  textAlign: 'left',
                                                  maxWidth: 360,
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                                  '&:hover': { textDecoration: 'underline' },
                                                }}
                                              >
                                                {att.fileName}
                                              </Typography>
                                              <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                                {formatFileSize(att.size)} · {new Date(att.createdAt).toLocaleDateString('da-DK')}
                                                {att.uploadedByName ? ` · ${att.uploadedByName}` : ''}
                                              </Typography>
                                              <Tooltip title="Download">
                                                <IconButton size="small" onClick={() => handleDownloadAttachment(offer.id, att)} sx={{ p: 0.25 }}>
                                                  <DownloadIcon sx={{ fontSize: 13 }} />
                                                </IconButton>
                                              </Tooltip>
                                              {canEditAttachments && (
                                                <Tooltip title="Slet vedhæftning">
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => setAttachmentToDelete({ offerId: offer.id, attachment: att })}
                                                    sx={{ p: 0.25 }}
                                                  >
                                                    <DeleteIcon sx={{ fontSize: 13 }} />
                                                  </IconButton>
                                                </Tooltip>
                                              )}
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                    </Box>
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                            </React.Fragment>
                          );
                        })
                      ) : !showNewOfferRow ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Typography variant="body2" color="text.secondary" py={1.5} sx={{ fontSize: '0.75rem' }}>
                              Ingen tilbud. Opret et tilbud først for at kunne vedhæfte filer.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </TabPanel>

            {/* Shipments Tab */}
            <TabPanel value={tabValue} index={3}>
              <Box px={1.5}>
                <TransportsTable customerId={id!} />
              </Box>
            </TabPanel>

            {/* Faste ruter Tab */}
            <TabPanel value={tabValue} index={4}>
              <Box px={1.5}>
                <TemplatesTable customerId={id!} region={customer.country === 'DE' ? 'de' : 'dk'} />
              </Box>
            </TabPanel>
          </Card>
        </Grid>
      </Grid>

      {/* Add Note Dialog */}
      <Dialog
        open={noteDialogOpen}
        onClose={() => setNoteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Tilføj note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            placeholder="Skriv din note her..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>Annuller</Button>
          <Button
            variant="contained"
            onClick={() => addNoteMutation.mutate(newNote)}
            disabled={!newNote.trim() || addNoteMutation.isPending}
          >
            {addNoteMutation.isPending ? 'Gemmer...' : 'Gem'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Tilføj kontaktperson</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box display="flex" gap={2}>
              <TextField
                size="small"
                label="Fornavn"
                value={newContact.firstName}
                onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label="Efternavn"
                value={newContact.lastName}
                onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })}
                fullWidth
              />
            </Box>
            <TextField
              size="small"
              label="Email"
              type="email"
              value={newContact.email}
              onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Telefon"
              value={newContact.phone}
              onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Stilling"
              value={newContact.title}
              onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Annuller</Button>
          <Button
            variant="contained"
            onClick={() => addContactMutation.mutate(newContact)}
            disabled={!newContact.firstName || !newContact.lastName || addContactMutation.isPending}
          >
            {addContactMutation.isPending ? 'Gemmer...' : 'Gem'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Activity Dialog */}
      <Dialog
        open={editActivityDialogOpen}
        onClose={() => setEditActivityDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rediger aktivitet</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              size="small"
              label="Titel"
              value={editActivityData.title}
              onChange={(e) => setEditActivityData({ ...editActivityData, title: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Beskrivelse"
              multiline
              rows={3}
              value={editActivityData.description}
              onChange={(e) => setEditActivityData({ ...editActivityData, description: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Resultat"
              value={editActivityData.outcome}
              onChange={(e) => setEditActivityData({ ...editActivityData, outcome: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Næste skridt"
              value={editActivityData.nextSteps}
              onChange={(e) => setEditActivityData({ ...editActivityData, nextSteps: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditActivityDialogOpen(false)}>Annuller</Button>
          <Button
            variant="contained"
            onClick={() => editingActivity && updateActivityMutation.mutate({
              activityId: editingActivity.id,
              data: editActivityData,
            })}
            disabled={!editActivityData.title.trim() || updateActivityMutation.isPending}
          >
            {updateActivityMutation.isPending ? 'Gemmer...' : 'Gem'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog
        open={editContactDialogOpen}
        onClose={() => setEditContactDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rediger kontaktperson</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box display="flex" gap={2}>
              <TextField
                size="small"
                label="Fornavn"
                value={editContactData.firstName}
                onChange={(e) => setEditContactData({ ...editContactData, firstName: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label="Efternavn"
                value={editContactData.lastName}
                onChange={(e) => setEditContactData({ ...editContactData, lastName: e.target.value })}
                fullWidth
              />
            </Box>
            <TextField
              size="small"
              label="Email"
              type="email"
              value={editContactData.email}
              onChange={(e) => setEditContactData({ ...editContactData, email: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Telefon"
              value={editContactData.phone}
              onChange={(e) => setEditContactData({ ...editContactData, phone: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Stilling"
              value={editContactData.title}
              onChange={(e) => setEditContactData({ ...editContactData, title: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={editContactData.isPrimary}
                  onChange={(e) => setEditContactData({ ...editContactData, isPrimary: e.target.checked })}
                />
              }
              label="Primær kontaktperson"
              sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditContactDialogOpen(false)}>Annuller</Button>
          <Button
            variant="contained"
            onClick={() => editingContact && updateContactMutation.mutate({
              contactId: editingContact.id,
              data: editContactData,
            })}
            disabled={!editContactData.firstName || !editContactData.lastName || updateContactMutation.isPending}
          >
            {updateContactMutation.isPending ? 'Gemmer...' : 'Gem'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Slet {deletingItemType === 'activity' ? 'aktivitet' : deletingItemType === 'contact' ? 'kontaktperson' : 'tilbud'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Er du sikker på at du vil slette dette {deletingItemType === 'activity' ? 'aktivitet' : deletingItemType === 'contact' ? 'kontaktperson' : 'tilbud'}? Handlingen kan ikke fortrydes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Annuller</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteExecute}
            disabled={deleteActivityMutation.isPending || deleteContactMutation.isPending || deleteOfferMutation.isPending}
          >
            {(deleteActivityMutation.isPending || deleteContactMutation.isPending || deleteOfferMutation.isPending) ? 'Sletter...' : 'Slet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog
        open={editCustomerDialogOpen}
        onClose={() => setEditCustomerDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Rediger kunde</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              size="small"
              label="Firmanavn"
              value={customerFormData.name}
              onChange={(e) => setCustomerFormData({ ...customerFormData, name: e.target.value })}
              fullWidth
            />
            <Box display="flex" gap={2}>
              <TextField
                size="small"
                label="CVR / Momsnr."
                value={customerFormData.cvr}
                onChange={(e) => setCustomerFormData({ ...customerFormData, cvr: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label="Telefon"
                value={customerFormData.phone}
                onChange={(e) => setCustomerFormData({ ...customerFormData, phone: e.target.value })}
                fullWidth
              />
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                size="small"
                label="Email"
                type="email"
                value={customerFormData.email}
                onChange={(e) => setCustomerFormData({ ...customerFormData, email: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label="Website"
                value={customerFormData.website}
                onChange={(e) => setCustomerFormData({ ...customerFormData, website: e.target.value })}
                fullWidth
              />
            </Box>

            <Divider sx={{ my: 0.5 }} />
            <Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>Adresse</Typography>

            <TextField
              size="small"
              label="Adresse"
              value={customerFormData.address}
              onChange={(e) => setCustomerFormData({ ...customerFormData, address: e.target.value })}
              fullWidth
            />
            <Box display="flex" gap={2}>
              <TextField
                size="small"
                label="Postnr."
                value={customerFormData.postalCode}
                onChange={(e) => setCustomerFormData({ ...customerFormData, postalCode: e.target.value })}
                sx={{ width: '30%' }}
              />
              <TextField
                size="small"
                label="By"
                value={customerFormData.city}
                onChange={(e) => setCustomerFormData({ ...customerFormData, city: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label="Land"
                value={customerFormData.country}
                onChange={(e) => setCustomerFormData({ ...customerFormData, country: e.target.value })}
                sx={{ width: '30%' }}
              />
            </Box>

            <Divider sx={{ my: 0.5 }} />
            <Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>Faktureringsadresse</Typography>

            <TextField
              size="small"
              label="Faktureringsadresse"
              value={customerFormData.billingAddress}
              onChange={(e) => setCustomerFormData({ ...customerFormData, billingAddress: e.target.value })}
              fullWidth
            />
            <Box display="flex" gap={2}>
              <TextField
                size="small"
                label="Postnr."
                value={customerFormData.billingPostalCode}
                onChange={(e) => setCustomerFormData({ ...customerFormData, billingPostalCode: e.target.value })}
                sx={{ width: '30%' }}
              />
              <TextField
                size="small"
                label="By"
                value={customerFormData.billingCity}
                onChange={(e) => setCustomerFormData({ ...customerFormData, billingCity: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label="Land"
                value={customerFormData.billingCountry}
                onChange={(e) => setCustomerFormData({ ...customerFormData, billingCountry: e.target.value })}
                sx={{ width: '30%' }}
              />
            </Box>

            <Divider sx={{ my: 0.5 }} />

            <FormControl size="small" fullWidth>
              <InputLabel>Ansvarlig</InputLabel>
              <Select
                value={customerFormData.assignedToId}
                label="Ansvarlig"
                onChange={(e) => setCustomerFormData({ ...customerFormData, assignedToId: e.target.value })}
              >
                <MenuItem value=""><em>Ingen</em></MenuItem>
                {(users || []).map((u: any) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} {u.email ? `(${u.email})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Noter"
              multiline
              rows={3}
              value={customerFormData.notes}
              onChange={(e) => setCustomerFormData({ ...customerFormData, notes: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCustomerDialogOpen(false)}>Annuller</Button>
          <Button
            variant="contained"
            onClick={() => updateCustomerMutation.mutate({
              ...customerFormData,
              assignedToId: customerFormData.assignedToId || null,
            })}
            disabled={!customerFormData.name.trim() || updateCustomerMutation.isPending}
          >
            {updateCustomerMutation.isPending ? 'Gemmer...' : 'Gem'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog
        open={taskDialogOpen}
        onClose={() => setTaskDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Opret opgave</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              size="small"
              label="Titel"
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              size="small"
              label="Beskrivelse"
              multiline
              rows={3}
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Forfaldsdato"
              type="date"
              value={newTask.dueDate}
              onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
              fullWidth
              required
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Prioritet</InputLabel>
              <Select
                value={newTask.priority}
                label="Prioritet"
                onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
              >
                <MenuItem value="LOW">Lav</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="HIGH">Høj</MenuItem>
                <MenuItem value="URGENT">Urgent</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel shrink>Tildelt til</InputLabel>
              <Select
                value={newTask.assignedToId}
                label="Tildelt til"
                displayEmpty
                notched
                onChange={(e) => setNewTask({ ...newTask, assignedToId: e.target.value })}
              >
                <MenuItem value="">Mig selv</MenuItem>
                {users?.map((u: any) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.firstName} {u.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>Annuller</Button>
          <Button
            variant="contained"
            onClick={() => createTaskMutation.mutate(newTask)}
            disabled={!newTask.title.trim() || !newTask.dueDate || createTaskMutation.isPending}
          >
            {createTaskMutation.isPending ? 'Opretter...' : 'Opret'}
          </Button>
        </DialogActions>
      </Dialog>

      <TaskDrawer
        open={!!drawerTask}
        task={drawerTask}
        customers={customer ? ([{ id: customer.id, name: customer.name }] as any) : []}
        onClose={() => setDrawerTask(null)}
        onSave={(taskId, payload) => updateTaskMutation.mutate({ id: taskId, data: payload })}
        onDelete={(taskId) => deleteTaskMutation.mutate(taskId)}
        onToggleComplete={(task) => {
          if (task.isCompleted) uncompleteTaskMutation.mutate(task.id);
          else completeTaskMutation.mutate(task.id);
          setDrawerTask({ ...task, isCompleted: !task.isCompleted });
        }}
        saving={updateTaskMutation.isPending}
        deleting={deleteTaskMutation.isPending}
      />
    </Box>
  );
};

// Inline offer editor row — fields match Flowtale /api/v1/customerdb/offers 1:1
// Compact tabular layout: each cell is a borderless input that looks like cell text.
interface OfferEditRowProps {
  isNew?: boolean;
  formData: {
    offerDate: string;
    contactId: string;
    origin: string;
    destination: string;
    distanceKm: string;
    amount: string;
    currency: string;
    info: string;
  };
  setFormData: (data: any) => void;
  contacts: Contact[];
  onSave: () => void;
  onCancel: () => void;
  isPending: boolean;
}

const OfferEditRow: React.FC<OfferEditRowProps> = ({ formData, setFormData, contacts, onSave, onCancel, isPending }) => {
  const cellSx = { py: 0.25, px: 0.75, fontSize: '0.7rem', bgcolor: 'action.hover' };
  const inputSx = {
    '& input': { fontSize: '0.7rem', py: 0, px: 0 },
    '& .MuiInput-root': { fontSize: '0.7rem' },
  };
  const set = (k: string, v: any) => setFormData({ ...formData, [k]: v });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onSave();
    if (e.key === 'Escape') onCancel();
  };

  return (
    <TableRow sx={{ '& td': cellSx }} onKeyDown={handleKeyDown}>
      <TableCell>
        <TextField
          size="small"
          variant="standard"
          type="date"
          autoFocus
          value={formData.offerDate}
          onChange={(e) => set('offerDate', e.target.value)}
          sx={inputSx}
          InputProps={{ disableUnderline: true }}
        />
      </TableCell>
      <TableCell>
        <Select
          size="small"
          variant="standard"
          value={formData.contactId}
          onChange={(e) => set('contactId', e.target.value)}
          sx={{ fontSize: '0.7rem', width: '100%' }}
          disableUnderline
          displayEmpty
        >
          <MenuItem value="" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>(ingen)</MenuItem>
          {contacts.map((c) => (
            <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.7rem' }}>
              {c.firstName} {c.lastName}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          variant="standard"
          placeholder="DK 6330 Padborg"
          value={formData.origin}
          onChange={(e) => set('origin', e.target.value)}
          sx={inputSx}
          InputProps={{ disableUnderline: true }}
          fullWidth
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          variant="standard"
          placeholder="DE 20095 Hamburg"
          value={formData.destination}
          onChange={(e) => set('destination', e.target.value)}
          sx={inputSx}
          InputProps={{ disableUnderline: true }}
          fullWidth
        />
      </TableCell>
      <TableCell align="right">
        <TextField
          size="small"
          variant="standard"
          type="number"
          value={formData.distanceKm}
          onChange={(e) => set('distanceKm', e.target.value)}
          sx={{ ...inputSx, width: 50, '& input': { ...inputSx['& input'], textAlign: 'right' } }}
          InputProps={{ disableUnderline: true }}
        />
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
          <TextField
            size="small"
            variant="standard"
            type="number"
            value={formData.amount}
            onChange={(e) => set('amount', e.target.value)}
            sx={{ ...inputSx, width: 70, '& input': { ...inputSx['& input'], textAlign: 'right' } }}
            InputProps={{ disableUnderline: true }}
          />
          <Select
            size="small"
            variant="standard"
            value={formData.currency || 'DKK'}
            onChange={(e) => set('currency', e.target.value)}
            sx={{ fontSize: '0.65rem', minWidth: 44, '& .MuiSelect-select': { py: 0, pr: '14px !important' } }}
            disableUnderline
          >
            <MenuItem value="DKK" sx={{ fontSize: '0.7rem' }}>DKK</MenuItem>
            <MenuItem value="EUR" sx={{ fontSize: '0.7rem' }}>EUR</MenuItem>
            <MenuItem value="USD" sx={{ fontSize: '0.7rem' }}>USD</MenuItem>
            <MenuItem value="GBP" sx={{ fontSize: '0.7rem' }}>GBP</MenuItem>
            <MenuItem value="SEK" sx={{ fontSize: '0.7rem' }}>SEK</MenuItem>
            <MenuItem value="NOK" sx={{ fontSize: '0.7rem' }}>NOK</MenuItem>
          </Select>
        </Box>
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          variant="standard"
          placeholder="Info"
          value={formData.info}
          onChange={(e) => set('info', e.target.value)}
          sx={inputSx}
          InputProps={{ disableUnderline: true }}
          fullWidth
        />
      </TableCell>
      <TableCell>
        <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontStyle: 'italic' }}>
          DRAFT
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <IconButton size="small" onClick={onSave} disabled={isPending} color="success" sx={{ p: 0.25 }}>
          <CheckCircleIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton size="small" onClick={onCancel} sx={{ p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

// Transport detail drawer component
const TransportDetailDrawer: React.FC<{
  transportId: string | null;
  onClose: () => void;
}> = ({ transportId, onClose }) => {
  const { data: transportData, isLoading } = useQuery({
    queryKey: ['transport-detail', transportId],
    queryFn: async () => {
      const res = await tmsApi.getTransportById(transportId!);
      return res.data.data as Transport;
    },
    enabled: !!transportId,
  });

  const t = transportData;

  const fmtAmount = (val?: number, currency?: string) => {
    if (val == null) return '-';
    return `${Number(val).toLocaleString('da-DK', { minimumFractionDigits: 2 })} ${currency || ''}`.trim();
  };

  return (
    <Drawer
      anchor="right"
      open={!!transportId}
      onClose={onClose}
      PaperProps={{ sx: { width: 450, maxWidth: '100vw' } }}
    >
      {isLoading || !t ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%" py={4}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                Booking #{t.exportBookingId || t.tmsReference}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                {t.date ? new Date(t.date).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {t.status && (
                <Chip label={t.status} size="small" sx={{ fontSize: '0.6rem', height: 20 }} />
              )}
              <IconButton size="small" onClick={onClose}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Rute section */}
          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 1 }}>Rute</Typography>
              <Box display="flex" alignItems="flex-start" gap={1}>
                <Box flex={1}>
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', mb: 0.25 }}>
                    Afhentning
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem' }}>
                    {t.origin || [t.originCity, t.originCountry].filter(Boolean).join(', ') || '-'}
                  </Typography>
                  {t.origin && (t.originCity || t.originCountry) && (
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                      {[t.originCity, t.originCountry].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                </Box>
                <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 1.5 }} />
                <Box flex={1}>
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', mb: 0.25 }}>
                    Levering
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem' }}>
                    {t.destination || [t.destinationCity, t.destinationCountry].filter(Boolean).join(', ') || '-'}
                  </Typography>
                  {t.destination && (t.destinationCity || t.destinationCountry) && (
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                      {[t.destinationCity, t.destinationCountry].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                </Box>
              </Box>
              {t.deliveryDate && (
                <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', mt: 0.75 }}>
                  Leveringsdato: {new Date(t.deliveryDate).toLocaleDateString('da-DK')}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Kunde section */}
          {t.customer && (
            <Card variant="outlined" sx={{ mb: 1.5 }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>Kunde</Typography>
                <Typography
                  component={RouterLink}
                  to={`/customers/${t.customer.id}`}
                  sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {t.customer.name}
                </Typography>
                {(t.tmsCustomerNumber || t.tmsCustomerName) && (
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', mt: 0.25 }}>
                    {[t.tmsCustomerNumber, t.tmsCustomerName].filter(Boolean).join(' - ')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Finansdata section */}
          {t.hasFinancialData ? (
            <Card variant="outlined" sx={{ mb: 1.5 }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 1 }}>Finansdata</Typography>
                <Grid container spacing={1}>
                  {[
                    { label: 'Beløb', value: fmtAmount(t.amount, t.currency) },
                    { label: 'Fragt', value: fmtAmount(t.freightAmount, t.currency) },
                    { label: 'Tillæg', value: fmtAmount(t.extraCostsAmount, t.currency) },
                    { label: 'Brændstof', value: fmtAmount(t.fuelSurchargeAmount, t.currency) },
                    { label: 'Maut', value: fmtAmount(t.mautSurchargeAmount, t.currency) },
                    { label: 'Moms', value: fmtAmount(t.vatAmount, t.currency) },
                  ].map(({ label, value }) => (
                    <Grid size={{ xs: 6 }} key={label}>
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{label}</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>{value}</Typography>
                    </Grid>
                  ))}
                  <Grid size={{ xs: 6 }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Faktura nr</Typography>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>{t.salesInvoice || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Faktura klar</Typography>
                    {t.invoiceReady != null ? (
                      <Chip
                        label={t.invoiceReady ? 'Ja' : 'Nej'}
                        size="small"
                        color={t.invoiceReady ? 'success' : 'default'}
                        sx={{ fontSize: '0.6rem', height: 18, mt: 0.25 }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>-</Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Valuta</Typography>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>{t.currency || '-'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 1.5, px: 1 }}>
              <InfoIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                Ingen finansdata tilknyttet denne booking
              </Typography>
            </Box>
          )}

          {/* Remarks */}
          {t.remarks && (
            <Card variant="outlined" sx={{ mb: 1.5 }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>Bemærkninger</Typography>
                <Typography sx={{ fontSize: '0.7rem', whiteSpace: 'pre-line' }}>{t.remarks}</Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Drawer>
  );
};

// Transports table component for the Shipments tab
const TransportsTable: React.FC<{ customerId: string }> = ({ customerId }) => {
  const [page, setPage] = useState(1);
  const [selectedTransportId, setSelectedTransportId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['transports', customerId, page],
    queryFn: async () => {
      const res = await customerApi.getTransports(customerId, { page, limit: 20 });
      return res.data;
    },
  });

  const transports: Transport[] = data?.data || [];
  const pagination = data?.pagination;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (transports.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" textAlign="center" py={2} sx={{ fontSize: '0.75rem' }}>
        Ingen forsendelser fundet
      </Typography>
    );
  }

  return (
    <Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Reference</TableCell>
              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Rute</TableCell>
              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Dato</TableCell>
              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }} align="right">Beløb</TableCell>
              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }} align="right">Fragt</TableCell>
              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Faktura</TableCell>
              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Bemærkninger</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transports.map((t) => (
              <TableRow key={t.id} hover onClick={() => setSelectedTransportId(t.id)} sx={{ cursor: 'pointer' }}>
                <TableCell sx={{ fontSize: '0.7rem' }}>{t.tmsReference}</TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>
                  {[t.originCity, t.originCountry].filter(Boolean).join(', ') || t.origin || '-'}
                  {' \u2192 '}
                  {[t.destinationCity, t.destinationCountry].filter(Boolean).join(', ') || t.destination || '-'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>
                  {t.date ? new Date(t.date).toLocaleDateString('da-DK') : '-'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>
                  <Chip label={t.status || 'Ukendt'} size="small" sx={{ fontSize: '0.6rem', height: 18 }} />
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                  {t.amount ? `${Number(t.amount).toLocaleString('da-DK', { minimumFractionDigits: 2 })} ${t.currency}` : '-'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                  {t.freightAmount ? `${Number(t.freightAmount).toLocaleString('da-DK', { minimumFractionDigits: 2 })} ${t.currency}` : '-'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>
                  {t.salesInvoice || '-'}
                  {t.invoiceReady && (
                    <Chip label="Klar" size="small" color="success" sx={{ ml: 0.5, fontSize: '0.55rem', height: 16 }} />
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.remarks || '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" gap={1} mt={1}>
          <Button
            size="small"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            sx={{ fontSize: '0.65rem' }}
          >
            Forrige
          </Button>
          <Typography sx={{ fontSize: '0.7rem', lineHeight: '30px' }}>
            Side {page} af {pagination.totalPages}
          </Typography>
          <Button
            size="small"
            disabled={page >= pagination.totalPages}
            onClick={() => setPage(page + 1)}
            sx={{ fontSize: '0.65rem' }}
          >
            Næste
          </Button>
        </Box>
      )}
      <TransportDetailDrawer
        transportId={selectedTransportId}
        onClose={() => setSelectedTransportId(null)}
      />
    </Box>
  );
};

// Templates table component for the Faste ruter tab
interface TemplateAddress {
  address_id: number;
  customer_name: string | null;
  address: string | null;
  city: string | null;
  zip_code: string | null;
  country_code: string | null;
}

interface CustomerTemplate {
  sales_order_id: number;
  description: string | null;
  fixed_price: number | null;
  currency_id: number | null;
  pickup_address: TemplateAddress | null;
  pickup_driver_info: string | null;
  delivery_address: TemplateAddress | null;
  booking_reference: string | null;
}

const currencyMap: Record<number, string> = { 1: 'DKK', 2: 'EUR', 3: 'USD', 4: 'SEK', 5: 'NOK', 6: 'GBP' };

const formatAddress = (addr: TemplateAddress | null): string => {
  if (!addr) return '-';
  const parts = [addr.customer_name, addr.address, [addr.zip_code, addr.city].filter(Boolean).join(' '), addr.country_code].filter(Boolean);
  return parts.join(', ');
};

const TemplatesTable: React.FC<{ customerId: string; region: string }> = ({ customerId, region }) => {
  const { data: templates, isLoading } = useQuery({
    queryKey: ['customer-templates', customerId, region],
    queryFn: async () => {
      const res = await tmsApi.getCustomerTemplates(customerId, region);
      return res.data.data as CustomerTemplate[];
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!templates || templates.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" textAlign="center" py={2} sx={{ fontSize: '0.75rem' }}>
        Ingen faste ruter fundet. Ruter vises efter en synkronisering med bookings.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 1 }}>
        {templates.length} faste ruter fra Transpas
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Beskrivelse</TableCell>
              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Afhentning</TableCell>
              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Levering</TableCell>
              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }} align="right">Fast pris</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((t) => (
              <TableRow key={t.sales_order_id} hover>
                <TableCell sx={{ fontSize: '0.7rem', maxWidth: 220 }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                    {t.description || `SO-${t.sales_order_id}`}
                  </Typography>
                  {t.pickup_driver_info && (
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', mt: 0.25, whiteSpace: 'pre-line', maxHeight: 40, overflow: 'hidden' }}>
                      {t.pickup_driver_info}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>
                  {t.pickup_address ? (
                    <Box>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                        {t.pickup_address.customer_name || '-'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                        {[t.pickup_address.address, [t.pickup_address.zip_code, t.pickup_address.city].filter(Boolean).join(' '), t.pickup_address.country_code].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>
                  {t.delivery_address ? (
                    <Box>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                        {t.delivery_address.customer_name || '-'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                        {[t.delivery_address.address, [t.delivery_address.zip_code, t.delivery_address.city].filter(Boolean).join(' '), t.delivery_address.country_code].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                  {t.fixed_price != null
                    ? `${Number(t.fixed_price).toLocaleString('da-DK', { minimumFractionDigits: 2 })} ${currencyMap[t.currency_id || 1] || ''}`
                    : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CustomerDetail;
